import React, { useCallback, useState, useRef, useEffect } from 'react';
import _ from 'underscore';
import { Icon, Popover, InputNumber, Select, Switch, Dropdown, Menu, Button } from 'antd';
import ColorPicker from '../../../artboard/ColorPicker';
import { useDocumentContext } from '../../../context/complexDocumentContext';
import { TABLE_TOOLBAR } from '../../../artboard/Toolbar/translationValues';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { CellStyleButtons } from '../styles';
import { ButtonSeparator, PickerContainer, ColorButton  } from '../../../artboard/Toolbar/styles';
import { ColumnSection, RowSection, PaddingOption, FieldTitle } from '../../../components/ComplexBlockSettings/styles';
import { measurementConversion } from '../../../utils/javascript';
import { getRangeOfCellsUUID } from '../complexDocumentHelper';

const NAMESPACES = ['TABLE_TOOLBAR'];
const { Option } = Select;

const DEBOUNCE_TIME_OUT = 300;

const CellOptions = ({
  tableRef,
  updateComplexElementDetails,
  element,
  selectedRange,
  flowUUID = null,
  creativeBlockUUID = null,
  staticTableBlock = false,
}) => {
  const [picker, setPicker] = useState(null);
  const [color, setColor] = useState(null);
  const [measurement, setMeasurement] = useState('px');
  const [persistedPadding, setPersistedPadding] = useState({});
  const [borderOptionsVisible, setBorderOptionsVisible] = useState(false);
  const changeInPadding = useRef(false);

  const customSelector = useCallback(() => null, []);
  const [, store] = useDocumentContext(customSelector, `CellOptions`);

  const { t } = useTranslation(NAMESPACES);

  useEffect(() => {
    if (changeInPadding.current) {
      changeInPadding.current = false;
      return;
    }
    const selectedCellStart = selectedRange?.start ? element.contents.find(el => el.uuid === selectedRange?.start) : undefined;
    const cellPadding = selectedCellStart?.style?.padding;
    const inMM = measurement === 'px';
    if (cellPadding) {
    setPersistedPadding({
      left: inMM ? cellPadding.left : measurementConversion(cellPadding.left, 'mm'),
      right: inMM ? cellPadding.right : measurementConversion(cellPadding.right, 'mm'),
      top: inMM ? cellPadding.top : measurementConversion(cellPadding.top, 'mm'),
      bottom: inMM ? cellPadding.bottom : measurementConversion(cellPadding.bottom, 'mm'),
    })
    }
    // TODO : setPersistedPadding to be called with updated values
  }, [element, selectedRange, measurement]);

  const debouncedUpdateRequest = useRef(_.debounce(updateComplexElementDetails, DEBOUNCE_TIME_OUT)).current;

  const incOrDecPadding = useCallback((value, key) => {
    let newPadding = undefined;
    setPersistedPadding((existingPadding) => {
      changeInPadding.current = true;
      if (key === 'all') {
        const allSideValue = measurement === 'px' ? value : measurementConversion(value, 'px');
        newPadding = {
          left: value,
          right: value,
          top: value,
          bottom: value
        };
      } else {
        newPadding = Object.assign({}, existingPadding, {
          [key]: measurement === 'px' ? value : measurementConversion(value, 'px')
        });
      }
      debouncedUpdateRequest(
        element.uuid,
        {
          selectedRange,
          format: 'padding',
          value: newPadding,
          tableRef,
        },
        'static-table-formatting'
      );
      return newPadding;
    });
  }, [measurement, selectedRange, element, tableRef]);

  const handleColorChange = useCallback((e) => {
    updateComplexElementDetails(
      element.uuid,
      {
        selectedRange,
        format: picker.item,
        value: e.hex
      },
      'static-table-formatting'
    );
    setPicker(existingPicker => ({ ...existingPicker, color: e.hex }));
  }, [picker, element, selectedRange, updateComplexElementDetails]);

  const toggleBorder = useCallback((e) => {
    const border = e.item.props.id;
    const enabled = e.item.props['data-enabled'];
      updateComplexElementDetails(
        element.uuid,
        {
          selectedRange,
          format: 'toggleBorder',
          value: enabled !== true,
          key: border
        },
        'static-table-formatting'
      );
  }, [element, selectedRange, updateComplexElementDetails]);

  const contentAlignment = useCallback((axis, position) => {
    const selectedCellUUIDs = getRangeOfCellsUUID(selectedRange.start, selectedRange.end, element.contents);
    const details = { flowUUID, selectedCellUUIDs, axis, position, op: 'update', ...( !staticTableBlock && { elementUUID: element.uuid })};
    const blockUUID = staticTableBlock ? element.uuid : creativeBlockUUID;
    const blockOperation = staticTableBlock ? 'static-table-content-alignment' : 'static-table-text-alignment';
    store.updateBlock(blockOperation, blockUUID, details);
  }, [element, selectedRange]);

  const selectedCellStart = selectedRange?.start ? element.contents.find(el => el.uuid === selectedRange?.start) : undefined;

  const backgroundColor = selectedCellStart?.style?.backgroundColor; // TODO : Get current values for background and border from the selectedRange
  const borderColor = selectedCellStart?.style?.border?.borderColor;

  const borderLeftEnabled = selectedCellStart?.style?.border?.left;
  const borderRightEnabled = selectedCellStart?.style?.border?.right;
  const borderTopEnabled = selectedCellStart?.style?.border?.top;
  const borderBottomEnabled = selectedCellStart?.style?.border?.bottom;
  const currentCellPadding = selectedCellStart?.style?.padding;
  const applyAllSides = currentCellPadding?.top === currentCellPadding?.bottom &&
                      currentCellPadding?.top === currentCellPadding?.right &&
                      currentCellPadding?.top === currentCellPadding?.left;

  const menuItems = () => {
    return (
      <Menu
        onMouseLeave={() => setBorderOptionsVisible(false)}
        className='static-table-border-options'
      >
        <Menu.Item id={borderLeftEnabled ? "hide-left" : "show-left"} data-enabled={borderLeftEnabled} onClick={toggleBorder}>
          <Icon type="border-left" />
          {borderLeftEnabled ? t(TABLE_TOOLBAR.HIDE_LEFT_BORDER_OPTION.key, TABLE_TOOLBAR.HIDE_LEFT_BORDER_OPTION.value) 
          : t(TABLE_TOOLBAR.SHOW_LEFT_BORDER_OPTION.key, TABLE_TOOLBAR.SHOW_LEFT_BORDER_OPTION.value)}
        </Menu.Item>
        <Menu.Item id={borderRightEnabled ? "hide-right" : "show-right"} data-enabled={borderRightEnabled} onClick={toggleBorder}>
          <Icon type="border-right" />
          {borderRightEnabled ? t(TABLE_TOOLBAR.HIDE_RIGHT_BORDER_OPTION.key, TABLE_TOOLBAR.HIDE_RIGHT_BORDER_OPTION.value)
          : t(TABLE_TOOLBAR.SHOW_RIGHT_BORDER_OPTION.key, TABLE_TOOLBAR.SHOW_RIGHT_BORDER_OPTION.value)}
        </Menu.Item>
        <Menu.Item id={borderTopEnabled ? "hide-top" : "show-top"} data-enabled={borderTopEnabled} onClick={toggleBorder}>
          <Icon type="border-top" />
          {borderTopEnabled ? t(TABLE_TOOLBAR.HIDE_TOP_BORDER_OPTION.key, TABLE_TOOLBAR.HIDE_TOP_BORDER_OPTION.value) 
          : t(TABLE_TOOLBAR.SHOW_TOP_BORDER_OPTION.key, TABLE_TOOLBAR.SHOW_TOP_BORDER_OPTION.value)}
        </Menu.Item>
        <Menu.Item id={borderBottomEnabled ? "hide-bottom" : "show-bottom"} data-enabled={borderBottomEnabled} onClick={toggleBorder}>
          <Icon type="border-bottom" />
          {borderBottomEnabled ? t(TABLE_TOOLBAR.HIDE_BOTTOM_BORDER_OPTION.key, TABLE_TOOLBAR.HIDE_BOTTOM_BORDER_OPTION.value)
          : t(TABLE_TOOLBAR.SHOW_BOTTOM_BORDER_OPTION.key, TABLE_TOOLBAR.SHOW_BOTTOM_BORDER_OPTION.value)}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item id="hide-all-inner" data-enabled={true} onClick={toggleBorder}>
          <Icon type="border-inner" />{t(TABLE_TOOLBAR.HIDE_INNER_BORDERS_OPTIONS.key, TABLE_TOOLBAR.HIDE_INNER_BORDERS_OPTIONS.value)}
        </Menu.Item>
        <Menu.Item id="show-all-inner" data-enabled={true} onClick={toggleBorder}>
          <Icon type="border-inner" />{t(TABLE_TOOLBAR.SHOW_INNER_BORDERS_OPTIONS.key, TABLE_TOOLBAR.SHOW_INNER_BORDERS_OPTIONS.value)}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item id="hide-all-outer" data-enabled={true} onClick={toggleBorder}>
          <Icon type="border-outer" />{t(TABLE_TOOLBAR.HIDE_OUTER_BORDERS_OPTIONS.key, TABLE_TOOLBAR.HIDE_OUTER_BORDERS_OPTIONS.value)}
        </Menu.Item>
        <Menu.Item id="show-all-outer" data-enabled={true} onClick={toggleBorder}>
          <Icon type="border-outer" />{t(TABLE_TOOLBAR.SHOW_OUTER_BORDERS_OPTIONS.key, TABLE_TOOLBAR.SHOW_OUTER_BORDERS_OPTIONS.value)}
        </Menu.Item>
      </Menu>
    );
  };

  const AlignmentOptions = () => {
    return (
      <div className="alignment-options">
        <h4>{t(TABLE_TOOLBAR.HORIZONTAL_ALIGNMENT_LABEL.key, TABLE_TOOLBAR.HORIZONTAL_ALIGNMENT_LABEL.value)}</h4>
        <Button.Group>
          <Button onClick={() => contentAlignment('x', 'left')}>
            <Icon type="align-left" />
          </Button>
          <Button onClick={() => contentAlignment('x', 'center')}>
            <Icon type="align-center" />
          </Button>
          <Button onClick={() => contentAlignment('x', 'right')}>
            <Icon type="align-right" />
          </Button>
        </Button.Group>
        <h4>{t(TABLE_TOOLBAR.VERTICAL_ALIGNMENT_LABEL.key, TABLE_TOOLBAR.VERTICAL_ALIGNMENT_LABEL.value)}</h4>
        <Button.Group>
          <Button onClick={() => contentAlignment('y', 'before')}>
            <Icon type="vertical-align-top" />
          </Button>
          <Button onClick={() => contentAlignment('y', 'center')}>
            <Icon type="vertical-align-middle" />
          </Button>
          <Button onClick={() => contentAlignment('y', 'after')}>
            <Icon type="vertical-align-bottom" />
          </Button>
        </Button.Group> 
      </div>
    );
  };

  return (
    <>
      <CellStyleButtons>
        <Tooltip
          placement="bottom"
          title={t(
            TABLE_TOOLBAR.CELL_BACKGROUND_COLOR.key,
            TABLE_TOOLBAR.CELL_BACKGROUND_COLOR.value,
          )}
        >
          <ColorButton
            style={{ background: backgroundColor || '#000' }}
            onClick={(e) => {
              e.persist();
              setPicker(existingPicker => {
                if (existingPicker?.item === 'background') {
                  return false;
                } else {
                  return ({
                    item: 'background',
                    color: backgroundColor || '#000',
                    target: e.target
                  })
                }
              });
            }}
          />
        </Tooltip>
        <Tooltip
          placement="bottom"
          title={t(
            TABLE_TOOLBAR.BORDER_COLOR_LABEL.key,
            TABLE_TOOLBAR.BORDER_COLOR_LABEL.value,
          )}
        >
          <ColorButton
            style={{ background: borderColor || '#000' }}
            onClick={(e) => {
              e.persist();
              setPicker(existingPicker => {
                if (existingPicker?.item === 'borderColor') {
                  return false;
                } else {
                  return ({
                    item: 'borderColor',
                    color: borderColor || '#000',
                    target: e.target
                  })
                }
              });
            }}
          />
        </Tooltip>
        <ButtonSeparator style={{ background: '#fff' }} />
        <Dropdown overlay={menuItems} placement="bottomCenter" visible={borderOptionsVisible}>
          <div className="border-options"
            onMouseOver={() => setBorderOptionsVisible(true)}
            onMouseLeave={(e) => {
              e.relatedTarget && typeof e.relatedTarget?.getAttribute === 'function' && !e.relatedTarget?.getAttribute("class")?.includes("ant-dropdown") && setBorderOptionsVisible(false);
            }}>
            {t(TABLE_TOOLBAR.BORDERS_LABEL.key, TABLE_TOOLBAR.BORDERS_LABEL.value)} <Icon type="down" 
            style={{ transform:  borderOptionsVisible ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'all 0.3s ease' }} />
          </div>
        </Dropdown>

        <ButtonSeparator style={{ background: '#fff' }} />
        <Popover content={<AlignmentOptions />} overlayClassName="static-table-alignment-options" trigger="hover">
            {t(TABLE_TOOLBAR.ALIGNMENT_LABEL.key, TABLE_TOOLBAR.ALIGNMENT_LABEL.value)}
        </Popover>

        <ButtonSeparator style={{ background: '#fff' }} />

        <Popover
          overlayClassName="cellPaddingPopover"
          content={
              <ColumnSection>
                <RowSection>
                  <FieldTitle isStyleChange={1}>
                    {t(TABLE_TOOLBAR.CELL_SPACING_LABEL.key, TABLE_TOOLBAR.CELL_SPACING_LABEL.value)}
                  </FieldTitle>
                  <Select value={measurement} style={{ width: 75 }} onChange={e => setMeasurement(e)}>
                    <Option value="mm">mm</Option>
                    <Option value="px">px</Option>
                  </Select>
                </RowSection>
                <RowSection>
                <PaddingOption>
                  <FieldTitle isSpacing={1}>
                    {t(TABLE_TOOLBAR.ALL_SIDES_LABEL.key, TABLE_TOOLBAR.ALL_SIDES_LABEL.value)}
                    <Switch checked={applyAllSides} />
                  </FieldTitle>
                  <InputNumber
                    min={0}
                    value={
                        applyAllSides
                        ? persistedPadding.top
                        : undefined
                    }
                    onChange={e => incOrDecPadding(e, 'all')}
                  />
                </PaddingOption>
                </RowSection>
                <RowSection>
                <PaddingOption>
                  <FieldTitle isSpacing={1}>
                    {t(TABLE_TOOLBAR.LEFT_LABEL.key, TABLE_TOOLBAR.LEFT_LABEL.value)}
                  </FieldTitle>
                  <InputNumber
                    min={0}
                    value={persistedPadding.left}
                    onChange={e => incOrDecPadding(e, 'left')}
                  />
                </PaddingOption>
                <PaddingOption>
                  <FieldTitle isSpacing={1}>
                    {t(TABLE_TOOLBAR.RIGHT_LABEL.key, TABLE_TOOLBAR.RIGHT_LABEL.value)}
                  </FieldTitle>
                  <InputNumber
                    min={0}
                    value={persistedPadding.right}
                    onChange={e => incOrDecPadding(e, 'right')}
                  />
                </PaddingOption>
                </RowSection>
                <RowSection>
                <PaddingOption>
                  <FieldTitle isSpacing={1}>
                    {t(TABLE_TOOLBAR.TOP_LABEL.key, TABLE_TOOLBAR.TOP_LABEL.value)}
                  </FieldTitle>
                  <InputNumber
                    min={0}
                    value={persistedPadding.top}
                    onChange={e => incOrDecPadding(e, 'top')}
                  />
                </PaddingOption>
                <PaddingOption>
                  <FieldTitle isSpacing={1}>
                    {t(TABLE_TOOLBAR.BOTTOM_LABEL.key, TABLE_TOOLBAR.BOTTOM_LABEL.value)}
                  </FieldTitle>
                  <InputNumber
                    min={0}
                    value={persistedPadding.bottom}
                    onChange={e => incOrDecPadding(e, 'bottom')}
                  />
                </PaddingOption>
                </RowSection>
              </ColumnSection>
          }
          trigger={"click"}
          placement={"bottom"}
        >
        <Tooltip
          placement="bottom"
          title={t(TABLE_TOOLBAR.PADDING_LABEL.key, TABLE_TOOLBAR.PADDING_LABEL.value)}
        >
          <button
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              color: '#000',
            }}
          >
            <Icon type="column-width" />
          </button>
        </Tooltip>
        </Popover>

        {picker && (
          <PickerContainer>
          <ColorPicker
            onChange={handleColorChange}
            onBlur={() => {
              setPicker(null);
            }}
            color={picker.color}
            qlEditor={true}
          />
          </PickerContainer>
        )}
      </CellStyleButtons>
    </>
  );
};

export default CellOptions;
